import { useEffect, useState } from "react";
import api, { fmtINR } from "@/lib/api";
import { whatsappSend } from "@/lib/pdf";
import { invoiceMessage } from "@/lib/messages";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Wallet, ArrowDownRight, ArrowUpRight, ShoppingCart, AlertTriangle,
  Bell, Plus, Search, MessageSquare, Printer, Filter, Clock, ChevronRight, CalendarX, BellRing, Truck
} from "lucide-react";

const KPI = ({ label, value, color, tid }) => (
  <div className="bg-white border border-border rounded-lg p-5 kpi-card" data-testid={tid}>
    <div className="overline">{label}</div>
    <div className={`text-3xl font-mono font-bold mt-2 ${color}`}>{value}</div>
  </div>
);

export default function Dashboard() {
  const nav = useNavigate();
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [searchRes, setSearchRes] = useState({ txns: [], parties: [] });

  const load = async () => {
    try {
      const { data } = await api.get(`/reports/dashboard?period=${period}`);
      setData(data);
    } catch (e) { toast.error("Failed to load dashboard"); }
  };
  useEffect(() => { load(); }, [period]);

  const search = async () => {
    if (!q) { setSearchRes({ txns: [], parties: [] }); return; }
    try {
      const [t, p] = await Promise.all([
        api.get(`/txns?q=${encodeURIComponent(q)}&limit=25`),
        api.get(`/parties?q=${encodeURIComponent(q)}`),
      ]);
      setSearchRes({ txns: t.data, parties: p.data });
    } catch (e) {}
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="overline">Dashboard</div>
          <h1 className="text-3xl font-bold tracking-tight">Overview</h1>
        </div>
        <div className="flex items-center gap-2 no-print">
          {["week", "month", "year"].map((p) => (
            <button key={p} onClick={() => setPeriod(p)} data-testid={`period-${p}`}
              className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-md border ${period === p ? "border-[#064E3B] bg-[#064E3B] text-white" : "border-border bg-white text-slate-700 hover:bg-slate-50"}`}>
              {p}
            </button>
          ))}
          <Button className="btn-primary ml-2" onClick={() => nav("/app/sales?new=1")} data-testid="dash-add-sale"><Plus size={14} /> Add Sale</Button>
          <Button variant="outline" onClick={() => nav("/app/purchases?new=1")} data-testid="dash-add-purchase"><Plus size={14} /> Add Purchase</Button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white border border-border rounded-lg p-4 mb-6 no-print">
        <div className="flex items-center gap-2">
          <Search size={16} className="text-muted-foreground" />
          <Input placeholder="Search transactions / parties / invoice number..." value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            data-testid="dash-search-input" />
          <Button onClick={search} className="btn-primary" data-testid="dash-search-btn">Search</Button>
        </div>
        {(searchRes.txns.length + searchRes.parties.length > 0) && (
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            <div>
              <div className="overline mb-2">Transactions</div>
              {searchRes.txns.map((t) => (
                <div key={t._id_str} className="flex items-center justify-between border-b border-border py-2 text-sm">
                  <div>
                    <div className="font-semibold">{t.number} · <span className="text-slate-500 uppercase">{t.txn_type}</span></div>
                    <div className="text-xs text-slate-500">{t.party_name} • {new Date(t.date).toLocaleDateString()}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono">{fmtINR(t.total)}</span>
                    <button onClick={() => window.open(`/print/txn/${t._id_str}`, "_blank")} data-testid={`sr-print-${t._id_str}`} className="p-1 hover:bg-slate-100 rounded"><Printer size={14} /></button>
                    <button onClick={async () => { const party = searchRes.parties.find(p => p._id_str === t.party_id); let firm = null; try { firm = (await api.get("/firms/me")).data; } catch {}; const body = invoiceMessage(t, firm); whatsappSend(party?.phone, body); }} data-testid={`sr-msg-${t._id_str}`} className="p-1 hover:bg-slate-100 rounded"><MessageSquare size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
            <div>
              <div className="overline mb-2">Parties</div>
              {searchRes.parties.map((p) => (
                <div key={p._id_str} className="flex items-center justify-between border-b border-border py-2 text-sm">
                  <div>
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.phone} · {p.kind}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => nav(`/app/parties/${p._id_str}`)}>Open</Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <KPI tid="kpi-receivables" label="Total Receivables" value={fmtINR(data?.receivables)} color="text-[#166534]" />
        <KPI tid="kpi-payables" label="Total Payables" value={fmtINR(data?.payables)} color="text-[#991B1B]" />
        <KPI tid="kpi-sales" label={`Sales (${period})`} value={fmtINR(data?.total_sales)} color="text-[#064E3B]" />
        <KPI tid="kpi-purchases" label={`Purchases (${period})`} value={fmtINR(data?.total_purchases)} color="text-slate-800" />
        <div
          className="bg-white border border-border rounded-lg p-5 kpi-card cursor-pointer hover:shadow-md transition"
          onClick={() => nav("/app/reports")}
          data-testid="kpi-overdue"
        >
          <div className="overline flex items-center gap-1"><Clock size={11} className="text-[#B45309]" /> Overdue Bills</div>
          <div className={`text-3xl font-mono font-bold mt-2 ${(data?.overdue_count || 0) > 0 ? "text-[#B45309]" : "text-slate-500"}`}>
            {data?.overdue_count || 0}
          </div>
          <div className="text-xs text-slate-500 mt-1 font-mono">{fmtINR(data?.overdue_amount || 0)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <button className="text-left bg-white border border-border rounded-lg p-5 kpi-card hover:shadow-md transition-shadow" onClick={() => nav("/app/items")} data-testid="kpi-expiry">
          <div className="overline flex items-center gap-1"><CalendarX size={11} className="text-[#991B1B]" /> Expired / Expiring (30d)</div>
          <div className="text-3xl font-mono font-bold mt-2"><span className="text-[#991B1B]">{data?.expired_count || 0}</span><span className="text-slate-300"> / </span><span className="text-[#B45309]">{data?.expiring_count || 0}</span></div>
        </button>
        <button className="text-left bg-white border border-border rounded-lg p-5 kpi-card hover:shadow-md transition-shadow" onClick={() => nav("/app/item-requests")} data-testid="kpi-reminders">
          <div className="overline flex items-center gap-1"><BellRing size={11} className="text-violet-700" /> Items to Purchase (customer requests)</div>
          <div className="text-3xl font-mono font-bold mt-2 text-violet-800">{data?.open_reminders || 0}</div>
        </button>
        <button className="text-left bg-white border border-border rounded-lg p-5 kpi-card hover:shadow-md transition-shadow" onClick={() => nav("/app/stock-transit")} data-testid="kpi-in-transit">
          <div className="overline flex items-center gap-1"><Truck size={11} className="text-blue-700" /> Stock in Transit</div>
          <div className="text-3xl font-mono font-bold mt-2 text-blue-800">{data?.in_transit_count || 0}</div>
        </button>
      </div>

      {(data?.expiry_alerts || []).length > 0 && (
        <div className="mt-6 bg-white border border-red-200 rounded-lg p-5" data-testid="expiry-panel">
          <div className="overline mb-3 flex items-center gap-2 text-[#991B1B]"><CalendarX size={12} /> Expiry Alerts — expired or expiring within 30 days</div>
          <table className="acc-table w-full">
            <thead><tr><th>Item</th><th>Batch</th><th>Expiry</th><th className="num">Qty</th><th>Status</th></tr></thead>
            <tbody>
              {data.expiry_alerts.map((e) => (
                <tr key={`${e.item_id}-${e.batch_no}`} data-testid={`expiry-row-${e.item_id}`}>
                  <td className="font-semibold">{e.item_name}</td>
                  <td className="font-mono text-xs">{e.batch_no}</td>
                  <td className="font-mono">{e.expiry_date}</td>
                  <td className="num">{e.qty} {e.unit}</td>
                  <td>{e.expired ? <span className="chip chip-red">Expired {Math.abs(e.days_left)}d ago</span> : <span className="chip chip-amber">{e.days_left === 0 ? "Expires today" : `In ${e.days_left}d`}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(data?.overdue_bills || []).length > 0 && (
        <div className="mt-6 bg-white border border-[#FDE68A] rounded-lg p-5" data-testid="overdue-panel">
          <div className="overline mb-3 flex items-center gap-2">
            <Clock size={12} className="text-[#B45309]" />
            Overdue Bills — Take Action
          </div>
          <table className="acc-table w-full">
            <thead>
              <tr>
                <th>Party</th><th>Invoice</th><th>Type</th>
                <th className="num">Balance</th><th className="num">Days Overdue</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {(data.overdue_bills || []).map((b) => (
                <tr key={b._id_str} data-testid={`overdue-row-${b._id_str}`}>
                  <td className="font-semibold">{b.party_name || "—"}</td>
                  <td className="font-mono">{b.number}</td>
                  <td>
                    <span className={`chip ${b.direction === "receivable" ? "chip-green" : "chip-red"} uppercase`}>
                      {b.direction === "receivable" ? "In" : "Out"}
                    </span>
                  </td>
                  <td className="num">{fmtINR(b.balance)}</td>
                  <td className="num text-[#B45309] font-semibold">{b.days_overdue}d</td>
                  <td>
                    <div className="flex gap-1">
                      <button
                        className="p-1 hover:bg-slate-100 rounded"
                        onClick={async () => {
                          let firm = null; try { firm = (await api.get("/firms/me")).data; } catch {}
                          const draft = `Dear ${b.party_name || "Sir/Madam"},\n\nInvoice ${b.number} is ${b.days_overdue} day(s) overdue. Outstanding balance: ₹${Number(b.balance).toFixed(2)}.\n\nKindly arrange the payment at the earliest.\n\nThank you.\n\n${firm?.firm_name || ""}`;
                          const body = window.prompt("WhatsApp text:", draft);
                          if (body) whatsappSend(b.party_phone, body);
                        }}
                        data-testid={`overdue-wa-${b._id_str}`}
                        title="WhatsApp"
                      >
                        <MessageSquare size={14} />
                      </button>
                      <button
                        className="p-1 hover:bg-slate-100 rounded"
                        onClick={() => nav(b.direction === "receivable" ? "/app/pay-in" : "/app/pay-out")}
                        data-testid={`overdue-pay-${b._id_str}`}
                        title="Record payment"
                      >
                        <ChevronRight size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 bg-white border border-border rounded-lg p-5">
          <div className="overline mb-3">Recent Sales</div>
          <table className="acc-table w-full">
            <thead><tr><th>Date</th><th>Invoice</th><th>Party</th><th className="num">Total</th><th className="num">Balance</th></tr></thead>
            <tbody>
              {(data?.recent_sales || []).map((s) => (
                <tr key={s._id_str}>
                  <td>{new Date(s.date).toLocaleDateString()}</td>
                  <td className="font-mono">{s.number}</td>
                  <td>{s.party_name || "—"}</td>
                  <td className="num">{fmtINR(s.total)}</td>
                  <td className="num">{fmtINR(s.balance)}</td>
                </tr>
              ))}
              {(!data?.recent_sales || data.recent_sales.length === 0) && <tr><td colSpan={5} className="text-center text-muted-foreground py-6">No sales yet</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="bg-white border border-border rounded-lg p-5">
          <div className="overline mb-3 flex items-center gap-2"><AlertTriangle size={12} className="text-[#D97706]" /> Low Stock Alerts</div>
          {(data?.low_stock_items || []).length === 0 && <div className="text-sm text-muted-foreground">All items are above minimum stock levels.</div>}
          {(data?.low_stock_items || []).map((it) => (
            <div key={it._id_str} className="flex items-center justify-between border-b border-border py-2 text-sm">
              <div>
                <div className="font-semibold">{it.name}</div>
                <div className="text-xs text-slate-500">Min {it.min_stock} · Stock {it.stock}</div>
              </div>
              <span className="chip chip-red font-mono">{it.stock}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
